import type { VesselCardEvaluationRequest } from "../models/vessel-card-evaluation.models";
import type {
  VesselCardDashboard,
  VesselCardDashboardResponse,
  VesselCardPerformanceRow,
  VesselCardVesselOption,
} from "../models/vessel-card.models";

// Synthetic CP inputs only; the displayed comparisons are recalculated from current expected performance.
// Stored comparison preview values are not used for the rating or adjusted speed.
const imosInputPerformance: VesselCardPerformanceRow[] = [
  {
    mode: "Ballast ECO",
    expectedSpeed: 12.1,
    expectedConsumption: 15.5,
    cpSpeed: 12.0,
    cpConsumption: 16.4,
    expectedAtCpSpeed: 12.3,
    expectedAtCpConsumption: 16.4,
    indicator: "Within Speed Abt",
    indicatorTone: "warning",
  },
  {
    mode: "Laden ECO",
    expectedSpeed: 11.4,
    expectedConsumption: 15.5,
    cpSpeed: 11.5,
    cpConsumption: 16.7,
    expectedAtCpSpeed: 11.5,
    expectedAtCpConsumption: 16.7,
    indicator: "Within Speed Abt",
    indicatorTone: "warning",
  },
  {
    mode: "Ballast FULL",
    expectedSpeed: 14.1,
    expectedConsumption: 22.8,
    cpSpeed: 14.0,
    cpConsumption: 22.2,
    expectedAtCpSpeed: 14.0,
    expectedAtCpConsumption: 22.2,
    indicator: "Within Speed Abt",
    indicatorTone: "warning",
  },
  {
    mode: "Laden FULL",
    expectedSpeed: 13.3,
    expectedConsumption: 22.8,
    cpSpeed: 13.0,
    cpConsumption: 23.2,
    expectedAtCpSpeed: 13.4,
    expectedAtCpConsumption: 23.2,
    indicator: "Marginally Better Than CP",
    indicatorTone: "neutral",
  },
];

const customCpPreviewPerformance: VesselCardPerformanceRow[] = [
  {
    mode: "Ballast ECO",
    expectedSpeed: 12.1,
    expectedConsumption: 15.5,
    cpSpeed: 12.2,
    cpConsumption: 16.2,
    expectedAtCpSpeed: 12.3,
    expectedAtCpConsumption: 16.2,
    indicator: "Within Speed Abt",
    indicatorTone: "warning",
  },
  {
    mode: "Laden ECO",
    expectedSpeed: 11.4,
    expectedConsumption: 15.5,
    cpSpeed: 11.3,
    cpConsumption: 16.1,
    expectedAtCpSpeed: 11.5,
    expectedAtCpConsumption: 16.1,
    indicator: "Marginally Better Than CP",
    indicatorTone: "neutral",
  },
  {
    mode: "Ballast FULL",
    expectedSpeed: 14.1,
    expectedConsumption: 22.8,
    cpSpeed: 14.2,
    cpConsumption: 22.9,
    expectedAtCpSpeed: 14.2,
    expectedAtCpConsumption: 22.9,
    indicator: "Within Speed Abt",
    indicatorTone: "warning",
  },
  {
    mode: "Laden FULL",
    expectedSpeed: 13.3,
    expectedConsumption: 22.8,
    cpSpeed: 13.2,
    cpConsumption: 23.0,
    expectedAtCpSpeed: 13.4,
    expectedAtCpConsumption: 23.0,
    indicator: "Marginally Better Than CP",
    indicatorTone: "neutral",
  },
];

// Synthetic demonstration data, including the familiar vessel from the Power BI screenshot.
// Both fuel grades are explicitly supplied at the same demo rate; live data keeps its own separate IFO/LSF figures.
// The additional vessels are fictional; no values describe verified vessel performance.
export const vesselCardMockResponse: VesselCardDashboardResponse = {
  vessels: [
    { imo: "9707821", vesselName: "MV BUNUN DYNASTY" },
    { imo: "9000003", vesselName: "MV DEMO PANAMAX · Scrubber fitted" },
    { imo: "9000015", vesselName: "MV DEMO SUPRAMAX · Design proxy" },
    { imo: "9000027", vesselName: "MV DEMO · No performance data" },
    { imo: "9000039", vesselName: "MV DEMO PANAMAX · Scrubber conflict" },
  ],
  card: {
    vessel: { imo: "9707821", vesselName: "MV BUNUN DYNASTY" },
    performanceBasis: "OBS_Actual",
    evaluatedAt: "06-Oct-26",
    lastFixtureAt: "18-Sep-26",
    scrubberFitted: false,
    summerDwt: 37795,
    scrubberInformation: {
      scrubberImosType: "None",
      scrubberImosFitted: false,
      scrubberPerformanceSummaryFitted: false,
      scrubberConflict: false,
    },
    performance: [
      {
        mode: "Ballast ECO",
        expectedSpeed: 12.1,
        expectedConsumption: 15.5,
        cpSpeed: 12,
        cpConsumption: 16,
        cpConsumptionByFuel: { ifo: 16, lsf: 16 },
        expectedAtCpSpeed: 12.2,
        expectedAtCpConsumption: 16,
        indicator: "Marginally Better Than CP",
        indicatorTone: "neutral",
      },
      {
        mode: "Laden ECO",
        expectedSpeed: 11.4,
        expectedConsumption: 15.5,
        cpSpeed: 11.5,
        cpConsumption: 16.3,
        cpConsumptionByFuel: { ifo: 16.3, lsf: 16.3 },
        expectedAtCpSpeed: 11.8,
        expectedAtCpConsumption: 16.3,
        indicator: "Better Than CP",
        indicatorTone: "positive",
      },
      {
        mode: "Ballast FULL",
        expectedSpeed: 14.1,
        expectedConsumption: 22.8,
        cpSpeed: 14,
        cpConsumption: 21.8,
        cpConsumptionByFuel: { ifo: 21.8, lsf: 21.8 },
        expectedAtCpSpeed: 13.9,
        expectedAtCpConsumption: 21.8,
        indicator: "Within Speed Abt",
        indicatorTone: "warning",
      },
      {
        mode: "Laden FULL",
        expectedSpeed: 13.3,
        expectedConsumption: 22.8,
        cpSpeed: 13,
        cpConsumption: 22.8,
        cpConsumptionByFuel: { ifo: 22.8, lsf: 22.8 },
        expectedAtCpSpeed: 13.3,
        expectedAtCpConsumption: 22.8,
        indicator: "Marginally Better Than CP",
        indicatorTone: "neutral",
      },
    ],
    imosInputPerformance,
    customCpPreviewPerformance,
    currentPerformance: {
      label: "Currently underperforming",
      speedDifference: 0.6,
      tone: "negative",
      lastHullInspectionOrCleaning: "12-Aug-26",
      lastDryDock: "06-Oct-25",
    },
    foulingFactor: "High",
    foulingTone: "negative",
    foulingDetails:
      "An 11-day warm-water idle stay after the August hull cleaning coincides with a 0.6 kn shortfall. Review the noon reports and hull inspection before the next fixture (demo observation).",
    tcoDescriptions: [
      {
        mode: "Ballast ECO",
        aggressiveSpeed: 12.6,
        aggressiveConsumption: 15.5,
        veryAggressiveSpeed: 12.8,
        veryAggressiveConsumption: 15.5,
      },
      {
        mode: "Laden ECO",
        aggressiveSpeed: 11.9,
        aggressiveConsumption: 15.5,
        veryAggressiveSpeed: 12.1,
        veryAggressiveConsumption: 15.5,
      },
      {
        mode: "Ballast FULL",
        aggressiveSpeed: 14.6,
        aggressiveConsumption: 22.8,
        veryAggressiveSpeed: 14.8,
        veryAggressiveConsumption: 22.8,
      },
      {
        mode: "Laden FULL",
        aggressiveSpeed: 13.8,
        aggressiveConsumption: 22.8,
        veryAggressiveSpeed: 14.0,
        veryAggressiveConsumption: 22.8,
      },
    ],
    comments: [
      "Demo IMOS lookup: latest fixture CP description dated 18-Sep-26. Switch to IMOS Input or Custom CP to compare alternative terms against the same expected performance.",
      "Demo TCO: Aggressive adds 0.5 kn; Very Aggressive converts the 5% consumption tolerance into extra speed and rounds the final speed down to one decimal place. Both keep expected consumption unchanged. Review all four operating modes before quoting.",
      "Demo dry-dock lookup: 06-Oct-25 from the sample docking report. Supporting docking and noon-report files are shown under Request Performance Evaluation.",
      "For a similar-sized Baltic reference select BHSI 38. Graph view shows separate ballast and laden comparisons; Expected vs ZN Model also includes stale, missing and partial prediction examples.",
    ],
    vesselParticulars: [
      { label: "Vessel Type", value: "Bulk Carrier" },
      { label: "Built / Age", value: "2014 / 12 yo" },
      { label: "Summer DWT", value: "37795 MT DWT ON 10.54 M SSW MT" },
      { label: "LOA", value: "179.97 m" },
      { label: "Beam", value: "29.8 m" },
      { label: "TPC", value: "48.6 t/cm" },
      { label: "Grain Capacity", value: "46994 m³" },
    ],
    engineAndDesign: [
      { label: "Engine Type", value: "6S46ME" },
      { label: "Yard", value: "Imabari Shbldg - Imabari" },
      { label: "Country Build", value: "Japan" },
      { label: "Design", value: "IS Bari-Star 38" },
      { label: "Scrubber Fitted", value: "No" },
      { label: "Holds / Hatches", value: "5 holds, 5 hatches" },
      { label: "Int. Gross / Net", value: "23281 / 12101" },
    ],
    rightShip: [
      { label: "Safety Score / GHG", value: "3 / C" },
      { label: "D.O.C Score", value: "3" },
      { label: "RS Last Outcome", value: "acceptable" },
      { label: "RS Inspection Valid To", value: "11 Jan 2027 (demo)" },
      { label: "Flag", value: "Panama" },
    ],
    ownership: [
      { label: "Beneficial Owner", value: "Wisdom Marine Lines SA | Chinese Taipei" },
      { label: "Registered Owner", value: "Bunun Dynasty SA | Singapore" },
      { label: "Ship Manager", value: "Union Marine Mgmt Serv Pte Ltd | Singapore" },
    ],
    idleStays: [
      {
        idleFrom: "2026-09-01",
        idleTo: "2026-09-12",
        daysIdle: 11,
        foulingExposure: "High",
        draft: 7.1,
        temperature: 28,
        location: "Singapore",
      },
      {
        idleFrom: "2026-09-23",
        idleTo: "2026-09-26",
        daysIdle: 3,
        foulingExposure: "Low",
        draft: 10.2,
        temperature: 16,
        location: "Rotterdam",
      },
    ],
  },
};

// Wider separations make CP and Baltic graph differences visible for a Panamax-sized ship.
const panamaxPerformance: VesselCardPerformanceRow[] = [
  {
    mode: "Ballast ECO",
    expectedSpeed: 12.5,
    expectedConsumption: 20,
    cpSpeed: 12.5,
    cpConsumption: 23,
    cpConsumptionByFuel: { ifo: 23, lsf: 23 },
    expectedAtCpSpeed: 13.1,
    expectedAtCpConsumption: 23,
    indicator: "Better Than CP",
    indicatorTone: "positive",
  },
  {
    mode: "Laden ECO",
    expectedSpeed: 11.5,
    expectedConsumption: 22,
    cpSpeed: 11.5,
    cpConsumption: 22,
    cpConsumptionByFuel: { ifo: 22, lsf: 22 },
    expectedAtCpSpeed: 11.5,
    expectedAtCpConsumption: 22,
    indicator: "Within Speed Abt",
    indicatorTone: "warning",
  },
  {
    mode: "Ballast FULL",
    expectedSpeed: 14,
    expectedConsumption: 34,
    cpSpeed: 14,
    cpConsumption: 31,
    cpConsumptionByFuel: { ifo: 31, lsf: 31 },
    expectedAtCpSpeed: 13.5,
    expectedAtCpConsumption: 31,
    indicator: "Worse Than CP",
    indicatorTone: "negative",
  },
  {
    mode: "Laden FULL",
    expectedSpeed: 13.5,
    expectedConsumption: 29,
    cpSpeed: 13.5,
    cpConsumption: 33,
    cpConsumptionByFuel: { ifo: 33, lsf: 33 },
    expectedAtCpSpeed: 14.1,
    expectedAtCpConsumption: 33,
    indicator: "Better Than CP",
    indicatorTone: "positive",
  },
];

const panamaxCard: VesselCardDashboard = {
  ...vesselCardMockResponse.card,
  vessel: vesselCardMockResponse.vessels[1],
  scrubberFitted: true,
  summerDwt: 82500,
  scrubberInformation: {
    scrubberImosType: "Hybrid",
    scrubberImosFitted: true,
    scrubberPerformanceSummaryFitted: true,
    scrubberConflict: false,
  },
  performance: panamaxPerformance,
  imosInputPerformance: [
    {
      ...panamaxPerformance[0],
      cpSpeed: 13,
      cpConsumption: 21,
      expectedAtCpSpeed: 12.7,
      expectedAtCpConsumption: 21,
      indicator: "Worse Than CP",
      indicatorTone: "negative",
    },
    {
      ...panamaxPerformance[1],
      cpSpeed: 11.8,
      cpConsumption: 24,
      expectedAtCpSpeed: 11.9,
      expectedAtCpConsumption: 24,
      indicator: "Within Speed Abt",
      indicatorTone: "warning",
    },
    {
      ...panamaxPerformance[2],
      cpSpeed: 14.2,
      cpConsumption: 36,
      expectedAtCpSpeed: 14.3,
      expectedAtCpConsumption: 36,
      indicator: "Within Speed Abt",
      indicatorTone: "warning",
    },
    {
      ...panamaxPerformance[3],
      cpSpeed: 13.2,
      cpConsumption: 31,
      expectedAtCpSpeed: 13.8,
      expectedAtCpConsumption: 31,
      indicator: "Better Than CP",
      indicatorTone: "positive",
    },
  ],
  customCpPreviewPerformance: [
    {
      ...panamaxPerformance[0],
      cpSpeed: 12,
      cpConsumption: 19,
      expectedAtCpSpeed: 12.3,
      expectedAtCpConsumption: 19,
      indicator: "Marginally Better Than CP",
      indicatorTone: "neutral",
    },
    {
      ...panamaxPerformance[1],
      cpSpeed: 12,
      cpConsumption: 23,
      expectedAtCpSpeed: 11.7,
      expectedAtCpConsumption: 23,
      indicator: "Worse Than CP",
      indicatorTone: "negative",
    },
    {
      ...panamaxPerformance[2],
      cpSpeed: 13.5,
      cpConsumption: 33,
      expectedAtCpSpeed: 13.9,
      expectedAtCpConsumption: 33,
      indicator: "Marginally Better Than CP",
      indicatorTone: "neutral",
    },
    {
      ...panamaxPerformance[3],
      cpSpeed: 13,
      cpConsumption: 30,
      expectedAtCpSpeed: 13.7,
      expectedAtCpConsumption: 30,
      indicator: "Better Than CP",
      indicatorTone: "positive",
    },
  ],
  currentPerformance: {
    label: "Performing as expected",
    speedDifference: 0,
    tone: "positive",
    lastHullInspectionOrCleaning: "20-Sep-26",
    lastDryDock: "15-Jul-26",
  },
  foulingFactor: "Low",
  foulingTone: "positive",
  foulingDetails: "Recent dry dock and hull cleaning with only a short cool-water idle stay (demo observation).",
  tcoDescriptions: panamaxPerformance.map((row) => ({
    mode: row.mode,
    aggressiveSpeed: Number((row.expectedSpeed! + 0.5).toFixed(1)),
    aggressiveConsumption: row.expectedConsumption,
    veryAggressiveSpeed: Math.trunc((row.expectedSpeed! + 0.5) * Math.pow(1 / 0.95, 1 / 2.8) * 10) / 10,
    veryAggressiveConsumption: row.expectedConsumption,
  })),
  comments: [
    "Fictional Panamax demo. OBS_Actual locks the fitted scrubber to Yes and displays HSFO. The CP sources contain deliberately different sample terms.",
    "Select Baltic BPI to see better, equal and worse fuel consumption across the four modes. Toggle graph view to inspect the separated comparison points.",
    "Sample dry-dock report records 15-Jul-26. Request Performance Evaluation includes supporting attachments for the docking lookup and IMOS CP description.",
  ],
  vesselParticulars: [
    { label: "Vessel Type", value: "Bulk Carrier / Panamax (demo)" },
    { label: "Built / Age", value: "2019 / 7 yo" },
    { label: "Summer DWT", value: "82500 MT DWT ON 14.43 M SSW" },
    { label: "LOA", value: "229 m" },
    { label: "Beam", value: "32.26 m" },
    { label: "TPC", value: "72 t/cm" },
    { label: "Grain Capacity", value: "98000 m³" },
  ],
  engineAndDesign: [
    { label: "Engine Type", value: "6S60ME-C (demo)" },
    { label: "Yard", value: "Demo shipyard" },
    { label: "Country Build", value: "Japan" },
    { label: "Design", value: "Panamax 82 (demo)" },
    { label: "Scrubber Fitted", value: "Yes — hybrid EGCS (demo)" },
    { label: "Scrubber Installed", value: "15-Jul-26 (demo dry dock)" },
    { label: "Fuel Grade", value: "HSFO (demo)" },
    { label: "Holds / Hatches", value: "7 holds, 7 hatches" },
    { label: "Int. Gross / Net", value: "44000 / 27000" },
  ],
  rightShip: [
    { label: "Safety Score / GHG", value: "4 / B (demo)" },
    { label: "Flag", value: "Singapore (demo)" },
  ],
  ownership: [
    { label: "Beneficial Owner", value: "Demo Shipping Ltd (fictional)" },
    { label: "Ship Manager", value: "Demo Marine Management (fictional)" },
  ],
  idleStays: [
    {
      idleFrom: "2026-09-24",
      idleTo: "2026-09-26",
      daysIdle: 2,
      foulingExposure: "Low",
      draft: 9.2,
      temperature: 16,
      location: "Rotterdam",
    },
  ],
};

const proxyCard: VesselCardDashboard = {
  ...vesselCardMockResponse.card,
  vessel: vesselCardMockResponse.vessels[2],
  performanceBasis: "Design and Year",
  scrubberFitted: null,
  summerDwt: 58000,
  scrubberInformation: {
    scrubberImosType: null,
    scrubberImosFitted: null,
    scrubberPerformanceSummaryFitted: null,
    scrubberConflict: false,
  },
  currentPerformance: {
    label: "Awaiting vessel-specific evaluation",
    speedDifference: null,
    tone: "neutral",
    lastHullInspectionOrCleaning: null,
    lastDryDock: null,
  },
  foulingFactor: null,
  foulingTone: "neutral",
  foulingDetails: "No vessel-specific idle-stay history is available for this design-proxy example.",
  comments: [
    "Fictional design-proxy demo. Yes uses HSFO and corrects each original VLSFO consumption by ×1.03 + 0.6 t/day for this 58,000 DWT vessel. No restores the original VLSFO figures. Speed stays unchanged and fitted-vessel information remains unknown.",
    "Last dry-dock date is unavailable. Use Request Performance Evaluation to add a docking report and request vessel-specific performance data.",
    "The performance and TCO values are synthetic design estimates. Try BHSI 38 or BSI 63, both CP sources and the ZN model scenarios in table and graph views.",
  ],
  vesselParticulars: [
    { label: "Vessel Type", value: "Bulk Carrier / Supramax (demo)" },
    { label: "Built / Age", value: "2021 / 5 yo" },
    { label: "Summer DWT", value: "58000 MT DWT ON 12.8 M SSW" },
    { label: "LOA", value: "190 m" },
    { label: "Beam", value: "32.26 m" },
    { label: "TPC", value: "58 t/cm" },
    { label: "Grain Capacity", value: "72000 m³" },
  ],
  engineAndDesign: [
    { label: "Engine Type", value: "6S50ME-C (demo)" },
    { label: "Yard", value: "Demo shipyard" },
    { label: "Design", value: "Supramax 58 (demo)" },
    { label: "Scrubber Fitted", value: "—" },
    { label: "Holds / Hatches", value: "5 holds, 5 hatches" },
  ],
  rightShip: [{ label: "Safety Score / GHG", value: "Not available" }],
  ownership: panamaxCard.ownership,
  idleStays: [],
};

// Shows the two additional scrubber outcomes without changing the original actual and design demonstrations.
// No Data retains CP terms for reference but never supplies expected performance; a conflict keeps IMOS authoritative.
const noDataCard: VesselCardDashboard = {
  ...proxyCard,
  vessel: vesselCardMockResponse.vessels[3],
  performanceBasis: "No Data",
  performance: proxyCard.performance.map((row) => ({
    ...row,
    expectedSpeed: null,
    expectedConsumption: null,
    expectedAtCpSpeed: null,
    expectedAtCpConsumption: null,
    indicator: "Not available",
    indicatorTone: "neutral",
  })),
  tcoDescriptions: proxyCard.tcoDescriptions.map((row) => ({
    ...row,
    aggressiveSpeed: null,
    aggressiveConsumption: null,
    veryAggressiveSpeed: null,
    veryAggressiveConsumption: null,
  })),
  comments: [
    "Fictional No Data demo. Scrubber Yes / No changes HSFO / VLSFO only. Expected performance and TCO remain unavailable.",
  ],
};

const scrubberConflictCard: VesselCardDashboard = {
  ...panamaxCard,
  vessel: vesselCardMockResponse.vessels[4],
  scrubberInformation: {
    scrubberImosType: "Hybrid",
    scrubberImosFitted: true,
    scrubberPerformanceSummaryFitted: false,
    scrubberConflict: true,
  },
  comments: [
    "Fictional conflict demo. IMOS reports a fitted scrubber but the performance summary says No. The selection stays locked to IMOS Yes / HSFO and the warning identifies both source values.",
  ],
};

const mockCards: Record<string, VesselCardDashboard> = {
  [vesselCardMockResponse.card.vessel.imo]: vesselCardMockResponse.card,
  [panamaxCard.vessel.imo]: panamaxCard,
  [proxyCard.vessel.imo]: proxyCard,
  [noDataCard.vessel.imo]: noDataCard,
  [scrubberConflictCard.vessel.imo]: scrubberConflictCard,
};

export const getVesselCardMockResponse = (imo: string | null): VesselCardDashboardResponse => ({
  vessels: vesselCardMockResponse.vessels,
  card: mockCards[imo ?? ""] ?? vesselCardMockResponse.card,
});

// Pronounced, mixed deltas make the fictional vessels' ZN tables and graphs easy to compare.
export const VESSEL_CARD_DEMO_ZN_FACTORS: Record<string, Record<string, number>> = {
  [panamaxCard.vessel.imo]: { "Ballast ECO": 0.88, "Laden ECO": 1.1, "Ballast FULL": 0.9, "Laden FULL": 1.12 },
  [proxyCard.vessel.imo]: { "Ballast ECO": 1.1, "Laden ECO": 0.9, "Ballast FULL": 1.12, "Laden FULL": 0.88 },
};

// Metadata only: these fixtures never upload files, download live documents or notify DVP.
export const getVesselCardMockEvaluationRequests = (vessel: VesselCardVesselOption): VesselCardEvaluationRequest[] => [
  {
    requestId: `demo-${vessel.imo}-fixture`,
    createdAt: "2026-10-06T09:00:00Z",
    imo: vessel.imo,
    vesselName: vessel.vesselName,
    reason: "fixture_review",
    additionalDetails:
      "Demo request: compare the attached IMOS CP description with expected performance and the Baltic reference. Include Aggressive and Very Aggressive TCO terms for ballast and laden modes.",
    requestedBy: "Demo chartering user",
    notificationStatus: "not_configured",
    attachments: [
      {
        attachmentId: `demo-${vessel.imo}-cp`,
        fileName: "IMOS-CP-description-demo.pdf",
        contentType: "application/pdf",
        sizeBytes: 184320,
      },
      {
        attachmentId: `demo-${vessel.imo}-noon`,
        fileName: "Noon-reports-demo.csv",
        contentType: "text/csv",
        sizeBytes: 28672,
      },
    ],
  },
  {
    requestId: `demo-${vessel.imo}-dock`,
    createdAt: "2026-10-02T14:30:00Z",
    imo: vessel.imo,
    vesselName: vessel.vesselName,
    reason: "new_vessel_information",
    additionalDetails:
      "Demo request: check the sample docking report for the last dry-dock date and review scrubber information before updating the vessel-specific evaluation.",
    requestedBy: "Demo operations user",
    notificationStatus: "not_configured",
    attachments: [
      {
        attachmentId: `demo-${vessel.imo}-dock-report`,
        fileName: "Dry-dock-and-scrubber-report-demo.pdf",
        contentType: "application/pdf",
        sizeBytes: 1048576,
      },
    ],
  },
];
